import React from "react";
import {
  alpha,
  CardMedia,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun4 } from "../../../../helpers/dateTransform";
import Etiquin from "../../../../assets/images/Ethiquin/ethiquin_no_data.svg";

export const BitacoraTable = ({ bitacoraList = [] }) => {
  const [t, i18n] = useTranslation("report");
  const theme = useTheme();
  // Table columns
  const columns = React.useMemo(() => {
    return [
      { id: "event", label: t("Bitacora.event"), align: "left", minWidth: 160 },
      { id: "user", label: t("Bitacora.user"), align: "left", minWidth: 160 },
      { id: "date", label: t("Bitacora.date"), align: "left", minWidth: 240 },
    ];
  }, [t]);
  // Table rows
  const rows = React.useMemo(() => {
    if (!Array.isArray(bitacoraList)) return [];
    return bitacoraList.map((b) => ({
      id: b.id,
      event: t(`Rutas.${b.path}`),
      user: b.user,
      date: parseDateToLgComun4(b.created_at, i18n.language),
    }));
  }, [bitacoraList, t, i18n.language]);

  const hasRows = rows.length > 0;
  const hasMoreThan10Rows = rows.length > 15;

  return (
    <TableContainer
      sx={{
        height: "63.33vh",
        overflowY: "auto",
        overflowX: { xs: "auto", xl: "hidden" },
        border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.3)}`,
        borderTopLeftRadius: "6px",
        borderTopRightRadius: "6px",
      }}
    >
      {!hasRows ? (
        <Stack
          sx={{
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CardMedia
            component="img"
            image={Etiquin}
            alt="not_found"
            sx={{ height: "45%", width: "45%", objectFit: "contain" }}
          />
          <Typography
            variant="h5"
            textAlign="center"
            color="secondary"
            mt={1.5}
          >
            {t("ReportsList.no_results_found")}...
          </Typography>
        </Stack>
      ) : (
        <Table
          stickyHeader
          aria-label="bitacora table list"
          size={hasMoreThan10Rows ? "small" : "medium"}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    py: 1.25,
                    backgroundColor: theme.palette.blueGray50.main,
                  }}
                >
                  <Typography variant="h6">{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  transition: "transform 0.2s ease-in-out",
                  transformOrigin: "center",
                  "&:hover": {
                    transform: { xs: "none", xl: "scale(1.007)" },
                    boxShadow: theme.shadows[1],
                  },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{ maxWidth: 200, px: 0.5 }}
                  >
                    {row[col.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};
